---
title: "Przeglądy okresowe budynku — terminy, kary i kto może je wykonać"
description: "Przeglądy okresowe budynku terminy — obowiązkowe kontrole roczne, dwukrotne i pięcioletnie, wymagane uprawnienia, kary do 5000 zł i jak Pinveo dokumentuje wyniki."
date: "2026-06-07"
slug: "przeglady-okresowe-budynku-terminy-kary"
faq:
  - q: "Kiedy trzeba przeprowadzić przegląd roczny budynku?"
    a: "Przegląd roczny powinien być przeprowadzony co najmniej raz w roku. Branżową praktyką jest termin do końca maja, choć przepisy nie wskazują konkretnej daty dla budynków poniżej 2000 m² powierzchni zabudowy."
  - q: "Czy właściciel mieszkania musi sam robić przeglądy?"
    a: "W budynku wielorodzinnym obowiązek przeglądów spoczywa na zarządcy lub wspólnocie mieszkaniowej, nie na indywidualnym właścicielu lokalu. Właściciel domu jednorodzinnego nie jest objęty ustawowym obowiązkiem obowiązkowych przeglądów okresowych przewidzianych dla budynków wielorodzinnych."
  - q: "Jakie uprawnienia musi mieć osoba wykonująca przegląd?"
    a: "Uprawnienia budowlane w specjalności odpowiedniej do zakresu kontroli — konstrukcyjno-budowlane dla elementów budowlanych, instalacyjne dla instalacji. Kominy może sprawdzać mistrz kominiarski."
  - q: "Jak długo ważny jest protokół z przeglądu?"
    a: "Protokół jest dokumentem historycznym — nie traci ważności, ale obowiązek kolejnej kontroli (rocznej lub pięcioletniej) powstaje niezależnie od jego treści. Organy nadzoru sprawdzają, czy kontrole odbywają się w wymaganych terminach."
  - q: "Czy Pinveo generuje protokół zgodny z wymogami prawnymi?"
    a: "Pinveo generuje profesjonalny raport PDF z pełnym opisem kontroli, zdjęciami i lokalizacją usterek, który inspektor opatruje podpisem. Zgodność z wymaganiami prawnymi zależy od treści protokołu — Pinveo dostarcza narzędzie, uprawnionym inspektorem jest człowiek."
---

Przeglądy okresowe budynku terminy — znajomość harmonogramu obowiązkowych kontroli to podstawowy obowiązek każdego właściciela i zarządcy nieruchomości. Niedotrzymanie terminów naraża na kary finansowe i odpowiedzialność za stan techniczny obiektu.

---

## Obowiązkowe przeglądy budynku według Prawa budowlanego

Prawo budowlane nakłada na właścicieli i zarządców obiektów budowlanych obowiązek przeprowadzania **regularnych kontroli stanu technicznego**. Wyróżniamy trzy główne rodzaje przeglądów:

1. **Kontrola roczna** — co najmniej raz w roku.
2. **Kontrola dwukrotna w roku** — dla budynków o powierzchni zabudowy powyżej 2000 m².
3. **Kontrola pięcioletnia** — co najmniej raz na 5 lat.

Dodatkowo prawo przewiduje **kontrole doraźne** — przeprowadzane po katastrofach budowlanych, silnych wiatrach, intensywnych opadach śniegu lub innych zdarzeniach mogących wpłynąć na stan techniczny obiektu.

---

## Terminy przeglądów — szczegółowy harmonogram

### Kontrola roczna

Przeprowadzana **co najmniej raz w roku**. Obejmuje ocenę stanu technicznego elementów narażonych na:

- szkodliwe wpływy atmosferyczne (elewacje, dachy, rynny, obróbki blacharskie),
- niszczące działanie czynników zewnętrznych (fundamenty, mury oporowe),
- intensywną eksploatację (klatki schodowe, instalacje).

Zakres rocznej kontroli obejmuje ponadto:
- przewody kominowe (spalinowe, dymowe, wentylacyjne),
- instalacje gazowe,
- urządzenia zabezpieczające przed dachami.

Umownie przyjmuje się, że **kontrolę roczną należy przeprowadzić do końca maja** każdego roku — przed sezonem letnim.

### Kontrola dwukrotna w roku — budynki powyżej 2000 m²

Właściciele i zarządcy budynków o **powierzchni zabudowy przekraczającej 2000 m²** oraz innych obiektów budowlanych o powierzchni dachu przekraczającej 1000 m² są zobowiązani do przeprowadzania kontroli rocznej **dwa razy w roku** — w terminach do 31 maja i do 30 listopada.

Dodatkowa kontrola w listopadzie ma na celu przygotowanie budynku do sezonu zimowego i ocenę stanu dachu przed obciążeniem śniegiem.

### Kontrola pięcioletnia

Przeprowadzana **co najmniej raz na 5 lat**. Stanowi pełną ocenę stanu technicznego i przydatności do użytkowania, w tym:

- wszystkich elementów budynku (konstrukcja, elewacja, dach, instalacje),
- estetyki obiektu i jego otoczenia,
- instalacji elektrycznej i piorunochronnej (szczegółowo),
- przewodów instalacyjnych i sanitarnych.

---

## Kto może przeprowadzać przeglądy?

Kontrole stanu technicznego może przeprowadzać wyłącznie **osoba posiadająca uprawnienia budowlane** w odpowiedniej specjalności:

- uprawnienia konstrukcyjno-budowlane — dla elementów konstrukcyjnych i budowlanych,
- uprawnienia instalacyjne — dla instalacji elektrycznych, sanitarnych, gazowych,
- **mistrz kominiarski** — wyłącznie w zakresie przewodów kominiarskich i wentylacyjnych.

Właściciel lub zarządca **nie może samodzielnie** przeprowadzać obowiązkowych przeglądów okresowych budynku. Musi zlecić je osobie uprawnionej.

> **Disclaimer:** Pinveo jest narzędziem do dokumentacji terenowej i nie zastępuje uprawnionych osób wykonujących przeglądy. Decyzje o stanie technicznym obiektu podejmują wyłącznie osoby z odpowiednimi uprawnieniami budowlanymi.

---

## Kary za brak przeglądów

Niedopełnienie obowiązku przeprowadzenia przeglądu lub brak aktualnej dokumentacji kontroli może skutkować:

- **Grzywną do 5000 zł** nakładaną przez Powiatowego Inspektora Nadzoru Budowlanego (PINB) w trybie administracyjnym (art. 93 pkt 8 Prawa budowlanego).
- **Nakazem przeprowadzenia kontroli** z wyznaczeniem terminu.
- **Odpowiedzialnością karną** w przypadku zaniedbań prowadzących do zagrożenia życia lub zdrowia.
- Problemami przy sprzedaży lub wynajmie — brak aktualnych przeglądów może uniemożliwić uzyskanie świadectwa charakterystyki energetycznej lub ubezpieczenia obiektu.

Odpowiedzialność za przeprowadzenie i udokumentowanie przeglądów spoczywa na właścicielu lub zarządcy — nawet jeśli zlecił on wykonanie kontroli podmiotowi zewnętrznemu.

---

## Dokumentacja przeglądów — co musi zawierać protokół?

Każdy protokół z przeglądu powinien zawierać:

- datę i miejsce przeprowadzenia kontroli,
- dane obiektu (adres, numer działki, rok budowy),
- zakres przeprowadzonej kontroli,
- imię, nazwisko i numer uprawnień osoby wykonującej przegląd,
- opis stwierdzonych usterek z lokalizacją i oceną pilności,
- dokumentację fotograficzną z geolokalizacją,
- zalecenia i terminy usunięcia usterek,
- podpis osoby uprawnionej.

Protokoły wpisywane są do **książki obiektu budowlanego (KOB)** lub — dla budynków oddanych po 2023 r. — do systemu c-KOB GUNB, a kopie przechowuje się przez co najmniej 5 lat.

---

## Jak Pinveo wspiera dokumentację przeglądów?

Pinveo to mobilne narzędzie stworzone dla inspektorów i zarządców, które skraca czas dokumentowania każdej kontroli:

- **Markery na rzucie PDF** — inspektor zaznacza usterki bezpośrednio na planie budynku, z opisem i zdjęciem w jednym miejscu.
- **Zdjęcia z GPS i znacznikiem czasu** — automatyczne metadane spełniają wymagania dokumentacyjne bez dodatkowej pracy.
- **Formularze dostosowane do obowiązkowych przeglądów** — roczne i pięcioletnie z listą wszystkich wymaganych elementów kontroli.
- **Raport PDF gotowy do podpisu** — wygenerowany automatycznie po zakończeniu inspekcji.
- **Praca offline** — synchronizacja po powrocie z terenu, bez ryzyka utraty danych.

Dowiedz się więcej: [Przeglądy okresowe budynków z Pinveo](/zastosowania/przeglady-okresowe-budynkow).

Przeczytaj też: [Przegląd roczny budynku — co obejmuje i jak go przeprowadzić](/blog/przeglad-roczny-budynku-krok-po-kroku).

---

## Podsumowanie

Przeglądy okresowe budynku to obowiązek ustawowy z dokładnie określonymi terminami — roczne (lub dwukrotnie dla dużych obiektów), pięcioletnie i doraźne. Wykonać je może wyłącznie osoba z uprawnieniami budowlanymi, a brak dokumentacji grozi karą do 5000 zł. Pinveo przyspiesza cały proces dokumentowania — od formularza w terenie, przez zdjęcia z GPS, po gotowy raport PDF.

---

## FAQ

### Kiedy trzeba przeprowadzić przegląd roczny budynku?

Przegląd roczny powinien być przeprowadzony co najmniej raz w roku. Branżową praktyką jest termin do końca maja, choć przepisy nie wskazują konkretnej daty dla budynków poniżej 2000 m² powierzchni zabudowy.

### Czy właściciel mieszkania musi sam robić przeglądy?

W budynku wielorodzinnym obowiązek przeglądów spoczywa na zarządcy lub wspólnocie mieszkaniowej, nie na indywidualnym właścicielu lokalu. Właściciel domu jednorodzinnego nie jest objęty ustawowym obowiązkiem obowiązkowych przeglądów okresowych przewidzianych dla budynków wielorodzinnych.

### Jakie uprawnienia musi mieć osoba wykonująca przegląd?

Uprawnienia budowlane w specjalności odpowiedniej do zakresu kontroli — konstrukcyjno-budowlane dla elementów budowlanych, instalacyjne dla instalacji. Kominy może sprawdzać mistrz kominiarski.

### Jak długo ważny jest protokół z przeglądu?

Protokół jest dokumentem historycznym — nie traci ważności, ale obowiązek kolejnej kontroli (rocznej lub pięcioletniej) powstaje niezależnie od jego treści. Organy nadzoru sprawdzają, czy kontrole odbywają się w wymaganych terminach.

### Czy Pinveo generuje protokół zgodny z wymogami prawnymi?

Pinveo generuje profesjonalny raport PDF z pełnym opisem kontroli, zdjęciami i lokalizacją usterek, który inspektor opatruje podpisem. Zgodność z wymaganiami prawnymi zależy od treści protokołu — Pinveo dostarcza narzędzie, uprawnionym inspektorem jest człowiek.
